import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { deletePerson, getPeople, type PeopleKind } from '@/features/people/api/people';

export function PeoplePage({ kind }: { kind: PeopleKind }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState('');
  const query = useQuery({ queryKey: ['people', kind], queryFn: () => getPeople(kind) });
  const title = kind === 'technicians' ? 'Technicians' : 'Managers';
  const singular = kind === 'technicians' ? 'Technician' : 'Manager';
  const basePath = kind === 'technicians' ? '/technicians' : '/managers';
  const rows = useMemo(() => {
    const keyword = filter.trim().toLocaleLowerCase('en-GB');
    return (query.data ?? [])
      .filter(
        (person) =>
          !keyword ||
          person.name.toLocaleLowerCase('en-GB').includes(keyword) ||
          person.email.toLocaleLowerCase('en-GB').includes(keyword),
      )
      .sort((left, right) =>
        left.name.localeCompare(right.name, 'en-GB', { sensitivity: 'base' }),
      );
  }, [filter, query.data]);
  const remove = useMutation({
    mutationFn: (ids: number[]) => Promise.all(ids.map((id) => deletePerson(kind, id))),
    onSuccess: async () => {
      setSelectedIds(new Set());
      await queryClient.invalidateQueries({ queryKey: ['people', kind] });
    },
  });
  const allSelected = rows.length > 0 && rows.every((person) => selectedIds.has(person.id));
  const someSelected = rows.some((person) => selectedIds.has(person.id));
  const selectedId = selectedIds.size === 1 ? [...selectedIds][0] : null;

  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(rows.map((person) => person.id)));
  const togglePerson = (id: number) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Box sx={{ p: 1, minWidth: 0 }}>
      <Paper elevation={0} sx={{ ...paperSx, mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minHeight: 44, px: 1 }}>
          <IconButton size="small" aria-label="Back" onClick={() => history.back()}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
          {selectedIds.size === 0 && (
            <Button
              component={RouterLink}
              to={`${basePath}/new`}
              startIcon={<AddIcon />}
              sx={commandButtonSx}
            >
              New
            </Button>
          )}
          {selectedId !== null && (
            <>
              {kind === 'technicians' && (
                <Button
                  component={RouterLink}
                  to={`${basePath}/${selectedId}/clients`}
                  startIcon={<PeopleOutlineIcon />}
                  sx={commandButtonSx}
                >
                  Clients
                </Button>
              )}
              <Button
                component={RouterLink}
                to={`${basePath}/${selectedId}`}
                startIcon={<EditOutlinedIcon />}
                sx={commandButtonSx}
              >
                Edit
              </Button>
            </>
          )}
          {selectedIds.size > 0 && (
            <Button
              startIcon={<DeleteOutlineIcon />}
              disabled={remove.isPending}
              onClick={() => remove.mutate([...selectedIds])}
              sx={deleteCommandButtonSx}
            >
              Delete
            </Button>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => void query.refetch()}
            sx={commandButtonSx}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...paperSx, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1.5}
          sx={{ minHeight: 58, px: 2, py: 1 }}
        >
          <Typography component="h1" variant="h1" sx={{ mr: 'auto' }}>
            Active {title}
          </Typography>
          <Tooltip title={`Filter ${title.toLowerCase()} by name or email`}>
            <span>
              <IconButton aria-label="Filter options" disabled>
                <FilterAltOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            size="small"
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value);
              setSelectedIds(new Set());
            }}
            placeholder="Filter by keyword"
            aria-label={`Filter ${title.toLowerCase()} by keyword`}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: { xs: '100%', sm: 280 }, '& .MuiOutlinedInput-root': { height: 36 } }}
          />
        </Stack>
        <Divider />

        {query.isError && (
          <Alert severity="error" sx={{ m: 2 }}>
            The {title.toLowerCase()} list could not be loaded.
          </Alert>
        )}
        {remove.isError && (
          <Alert severity="error" sx={{ m: 2 }}>
            The selected {selectedIds.size === 1 ? singular.toLowerCase() : title.toLowerCase()} could not be deleted.
          </Alert>
        )}

        <TableContainer sx={{ minHeight: 360 }}>
          <Table
            size="small"
            aria-label={title}
            sx={{
              minWidth: 620,
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                height: 44,
                py: 1.1,
                borderBottom: '1px solid #e1dfdd',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={!allSelected && someSelected}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': `Select all ${title.toLowerCase()}` }}
                  />
                </TableCell>
                <TableCell sx={{ width: '42%', fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ height: 220 }}>
                    <CircularProgress size={28} aria-label={`Loading ${title.toLowerCase()}`} />
                  </TableCell>
                </TableRow>
              )}
              {!query.isLoading && !query.isError && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ height: 180, color: 'text.secondary' }}>
                    {filter ? `No ${title.toLowerCase()} match this keyword.` : `No active ${title.toLowerCase()} to show.`}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((person) => (
                <TableRow
                  key={person.id}
                  hover
                  selected={selectedIds.has(person.id)}
                  role="link"
                  tabIndex={0}
                  aria-label={`Open ${person.name}`}
                  onClick={() => void navigate(`${basePath}/${person.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void navigate(`${basePath}/${person.id}`);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(person.id)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => togglePerson(person.id)}
                      inputProps={{ 'aria-label': `Select ${person.name}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`${basePath}/${person.id}`}
                      underline="none"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {person.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={`mailto:${person.email}`}
                      underline="none"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {person.email}
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <Typography variant="body2" sx={{ px: 2, py: 1.25 }}>
          Rows: {rows.length}
        </Typography>
      </Paper>
    </Box>
  );
}

const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.14)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
const deleteCommandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'error.main' },
};

